import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Button, Drawer, IconNode, Tab, Tabs, ToolTip, Typography } from '@components/base';
import css from './index.module.scss';
import {
  fetchCustomerEnquiryPrefill,
  getEnquiryList,
  submitConsolidationQuote,
  submitNonConsolidationQuote
} from '@services/quote.service';
import notify from '@helpers/toastify-helper';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { DeletePrompt, Seo } from '@components/common';
import debounce from 'lodash.debounce';
import SkeletonQuoteListing from './components/skeleton';
import { fetchAllUsers } from '@services/user.service';
import AccessWrapper from '@authorization/access-wrapper';
import QuoteFormWrapper from '@components/common/quote-wrapper';
import EnquiryListing from './components/enquiry-listing';
import EnquiryDraftListing from './components/enquiry-draft';
import SaveToDraftPrompt from '@components/common/quote-wrapper/components/save-to-draft-confirmation-modal';
import { mapCustomerEnquiryToQuotePrefill } from '@components/common/quote-wrapper/components/helper';
import { hasNonZeroPriceComponent } from '@helpers/utils';
import { IEnquiryListItem, IUserInfo } from './quote-home';
import { ISelect } from '@helpers/types';
import { IQuotePrefill, QuoteFormData } from '@helpers/types/quotes';

interface IFetchEnquiryList {
  page?: number;
  search: string;
  created_by: string;
  is_draft: string;
  market_type?: string;
}

const ENQUIRY_MAPPING = [
  {
    value: 1,
    key: 'quote_table',
    default: true
  },
  {
    value: 2,
    key: 'cost_calculator'
  }
];

const getDefaultTab = () => {
  const defaultTab = ENQUIRY_MAPPING.find((tab) => tab.default);
  return defaultTab ? defaultTab : ENQUIRY_MAPPING[0];
};

const itemLimit = 10;

// Set by the DOMS v2 "Create Quote" button: /quote?source=customer_enquiry&customer_enquiry_id=<uuid>
const CUSTOMER_ENQUIRY_SOURCE = 'customer_enquiry';

const defaultTab = getDefaultTab();

export interface IQuoteListingState {
  isLoading: boolean;
  isSearchLoading: boolean;
  showQuoteForm: boolean;
  activeStep: number;
  enquiryCount: number;
  enquiryList: IEnquiryListItem[];
  user_list: IUserInfo[];
  currentPageNumber: number;
  searchText: string;
  created_by: IUserInfo | null;
  initialUserList: IUserInfo[];
  showDisclaimer: boolean;
  draftCount: number;
  market_type: ISelect | null;
  draftReqBody: QuoteFormData | null;
  consolidation: boolean;
}

function getValueByKey(key: string) {
  const state = ENQUIRY_MAPPING.find((item) => item.key === key);
  return state ? state.value : defaultTab.value;
}

const QuoteHome = (props: any) => {
  const { actions } = props.modulePermissions;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showQuoteFormParam = searchParams.get('showQuoteForm');
  const initialPageNumber = parseInt(searchParams.get('page') || '1', 10);
  const activeTabParam = searchParams.get('activeTab');
  const sourceParam = searchParams.get('source');
  const customerEnquiryIdParam = searchParams.get('customer_enquiry_id');
  const isCustomerEnquiryPrefill = sourceParam === CUSTOMER_ENQUIRY_SOURCE && !!customerEnquiryIdParam;
  const debounceSearch = useRef<any>();
  const initialOrderState = useMemo(() => {
    return getValueByKey(activeTabParam || defaultTab.key);
  }, [activeTabParam]);

  const [quoteState, setQuoteState] = useState<IQuoteListingState>({
    isLoading: false,
    isSearchLoading: false,
    // A prefill opens the form itself once loaded; the form only reads its values on mount
    showQuoteForm: showQuoteFormParam === 'true' && !isCustomerEnquiryPrefill,
    activeStep: initialOrderState,
    enquiryCount: 0,
    enquiryList: [],
    user_list: [],
    currentPageNumber: initialPageNumber,
    searchText: '',
    created_by: null,
    initialUserList: [],
    showDisclaimer: false,
    draftCount: 0,
    market_type: null,
    draftReqBody: null,
    consolidation: false
  });

  const [quotePrefill, setQuotePrefill] = useState<IQuotePrefill | null>(null);
  const [isPrefillLoading, setIsPrefillLoading] = useState(isCustomerEnquiryPrefill);

  const {
    isLoading,
    showQuoteForm,
    activeStep,
    enquiryCount,
    searchText,
    created_by,
    initialUserList,
    showDisclaimer,
    draftCount,
    market_type,
    draftReqBody,
    consolidation
  } = quoteState;

  useEffect(() => {
    if (activeTabParam) {
      const newTabValue = getValueByKey(activeTabParam);
      if (newTabValue !== activeStep) {
        setQuoteState((prev) => ({
          ...prev,
          activeStep: newTabValue,
          currentPageNumber: 1
        }));
      }
    }
  }, [activeTabParam]);

  const fetchCreatedByList = async () => {
    const userResponse = await fetchAllUsers({ role: 'internal' });
    if (userResponse.success) {
      const { data: user_list } = userResponse;
      setQuoteState((prevState: IQuoteListingState) => ({
        ...prevState,
        user_list: user_list.users,
        initialUserList: user_list.users
      }));
    } else {
      notify({ message: 'Unable to fetch order POC list', severity: 'error' });
    }
  };

  const filterCreatedBy = async (event: any, param: string) => {
    if (param && param != created_by?.given_name) {
      const userResponse = await fetchAllUsers({ role: 'internal', name: param });
      if (userResponse.success) {
        const { data: user_list } = userResponse;
        setQuoteState((prevState: IQuoteListingState) => ({
          ...prevState,
          user_list: user_list.users
        }));
      } else {
        setQuoteState((prevState: IQuoteListingState) => ({ ...prevState, isLoading: false }));
        notify({ message: 'Unable to fetch order POC list', severity: 'error' });
      }
    } else {
      setQuoteState((prevState: IQuoteListingState) => ({
        ...prevState,
        user_list: initialUserList
      }));
    }
  };

  const fetchEnquiryListWithCounts = async (params: IFetchEnquiryList) => {
    setQuoteState((prev) => ({ ...prev, isLoading: true }));

    const response = await getEnquiryList(params);

    if (response.success) {
      const currentTabCount = response.data.count;
      const isCurrentTabDraft = params?.is_draft == 'true';
      const oppositeTabParams = {
        is_draft: isCurrentTabDraft ? 'false' : 'true'
      };

      const oppositeTabResponse = await getEnquiryList(oppositeTabParams);

      if (oppositeTabResponse.success) {
        setQuoteState((prev) => ({
          ...prev,
          enquiryList: response.data.results,
          currentPageNumber: params?.page || 1,
          isSearchLoading: false,
          isLoading: false,
          enquiryCount: isCurrentTabDraft ? oppositeTabResponse.data.count : currentTabCount,
          draftCount: isCurrentTabDraft ? currentTabCount : oppositeTabResponse.data.count
        }));
      }
      setSearchParams({
        ...Object.fromEntries(searchParams),
        page: params?.page?.toString() || '1'
      });
    } else {
      setQuoteState((prev) => ({ ...prev, isLoading: false }));
      notify({
        severity: 'error',
        message: response?.error || 'Something Went Wrong'
      });
    }
  };

  const handleQuoteFormClick = () => {
    setQuoteState((prevState: IQuoteListingState) => ({
      ...prevState,
      showQuoteForm: !prevState.showQuoteForm
    }));
    setSearchParams({
      ...Object.fromEntries(searchParams),
      showQuoteForm: showQuoteForm ? 'false' : 'true'
    });
  };

  const handlePageChange = async (newPage: number) => {
    await fetchEnquiryListWithCounts({
      page: newPage,
      search: searchText,
      created_by: created_by?.user_id || '',
      is_draft: activeStep === 1 ? 'false' : 'true',
      market_type: market_type?.value
    });
  };

  const validatePageChange = (event: any, value: number) => {
    if (isNaN(value)) return;
    if (value === 0 || value > Math.ceil(activeStep === 1 ? enquiryCount : draftCount / itemLimit))
      return;
    handlePageChange(value);
  };

  const handleStepChange = async (_event: SyntheticEvent, newValue: number) => {
    setQuoteState((prev: IQuoteListingState) => ({
      ...prev,
      activeStep: newValue,
      currentPageNumber: 1,
      searchText: '',
      created_by: null,
      market_type: null
    }));
    setSearchParams(() => {
      return {
        ...Object.fromEntries(searchParams),
        activeTab: ENQUIRY_MAPPING.find((item) => item.value === newValue)?.key || ''
      };
    });
  };

  const fetchSearchResult = async (searchText: string) => {
    await fetchEnquiryListWithCounts({
      page: 1,
      search: searchText,
      created_by: created_by?.user_id || '',
      is_draft: activeStep === 1 ? 'false' : 'true',
      market_type: market_type?.value
    });
  };

  const handleOrderSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setQuoteState((prevState: IQuoteListingState) => ({
      ...prevState,
      currentPageNumber: 1,
      searchText: value.trim(),
      isSearchLoading: true
    }));
    debounceSearch.current?.cancel();
    debounceSearch.current = debounce(fetchSearchResult, 800);
    debounceSearch.current(value);
  };

  const openQuoteFormFromCustomerEnquiry = async (customerEnquiryId: string) => {
    const response = await fetchCustomerEnquiryPrefill(customerEnquiryId);

    let prefill: IQuotePrefill | null = null;
    if (response?.success && response.data) {
      try {
        prefill = mapCustomerEnquiryToQuotePrefill(customerEnquiryId, response.data);
      } catch (error) {
        prefill = null;
      }
    }

    // Drop the prefill params so a refresh doesn't prefill again
    const nextSearchParams = Object.fromEntries(searchParams);
    delete nextSearchParams.source;
    delete nextSearchParams.customer_enquiry_id;

    setIsPrefillLoading(false);
    if (prefill) {
      setQuotePrefill(prefill);
      setQuoteState((prevState: IQuoteListingState) => ({ ...prevState, showQuoteForm: true }));
      setSearchParams({ ...nextSearchParams, showQuoteForm: 'true' }, { replace: true });
    } else {
      notify({
        severity: 'error',
        message:
          response?.status === 404
            ? 'Customer enquiry not found or not assigned to you'
            : response?.error || 'Unable to load customer enquiry'
      });
      setSearchParams(nextSearchParams, { replace: true });
    }
  };

  useEffect(() => {
    fetchCreatedByList();
    if (isCustomerEnquiryPrefill) openQuoteFormFromCustomerEnquiry(customerEnquiryIdParam);
  }, []);

  useEffect(() => {
    if (!showQuoteForm) setQuotePrefill(null);
  }, [showQuoteForm]);

  useEffect(() => {
    const currentPage = Number(searchParams.get('page') || '1');
    const currentTabKey = activeTabParam || defaultTab.key;
    const isDraftParam = currentTabKey === 'cost_calculator' ? 'true' : 'false';

    // Skip while a customer enquiry prefill is loading; clearing the flag re-runs this effect
    if (showQuoteFormParam === 'true' || isPrefillLoading) {
      return;
    }

    fetchEnquiryListWithCounts({
      page: currentPage,
      search: searchText,
      created_by: created_by?.user_id || '',
      is_draft: isDraftParam,
      market_type: market_type?.value
    });
  }, [created_by, market_type, activeTabParam, searchParams, isPrefillLoading]);

  const onClose = (data: any) => {
    console.log(data);
    setQuoteState((prevState: IQuoteListingState) => ({
      ...prevState,
      showDisclaimer: true,
      draftReqBody: data,
      consolidation: data?.is_consolidation
    }));
  };

  const saveAsDraft = async () => {
    if (!draftReqBody) {
      notify({
        severity: 'error',
        message: 'No form data available to save'
      });
      return;
    }
    const draftValidation = hasNonZeroPriceComponent(draftReqBody);
    if (!draftValidation) {
      notify({
        severity: 'error',
        message: 'Atleast One Price is Required'
      });
      return;
    }
    const response = consolidation
      ? await submitConsolidationQuote(draftReqBody)
      : await submitNonConsolidationQuote(draftReqBody);

    if (response.success) {
      notify({
        severity: 'success',
        message: response.data.message
      });
      setQuoteState((prevState: IQuoteListingState) => ({
        ...prevState,
        showDisclaimer: false,
        showQuoteForm: false,
        draftReqBody: null
      }));
      const currentTabKey = activeTabParam || defaultTab.key;
      if (currentTabKey === 'cost_calculator') {
        fetchEnquiryListWithCounts({
          page: 1,
          search: searchText,
          created_by: created_by?.user_id || '',
          is_draft: 'true',
          market_type: market_type?.value
        });
      } else {
        navigate('/quote?activeTab=cost_calculator&showQuoteForm=false', { replace: true });
      }
    } else {
      notify({
        severity: 'error',
        message: `Somthing Went Wrong!!`
      });
    }
  };

  return (
    <AccessWrapper show={actions?.read} showUnauthorised>
      <main className={css.mainWrapper}>
        <Seo title="Quotes" />
        <section className={css.enquiryHeader}>
          <div className={css.enquiryHeaderTitle}>
            <Tabs value={activeStep} onChange={handleStepChange}>
              <Tab
                label={
                  <div className={css.titleTextIcon}>
                    <Typography variant="h2">Quotes</Typography>
                    {enquiryCount > 0 && <Badge>{enquiryCount}</Badge>}
                  </div>
                }
                value={1}
              />
              <Tab
                label={
                  <div className={css.titleTextIcon}>
                    <Typography variant="h2">Cost Calculator</Typography>
                    {draftCount > 0 && <Badge>{draftCount}</Badge>}
                  </div>
                }
                value={2}
              />
            </Tabs>
          </div>
          <AccessWrapper show={actions?.create}>
            <Button
              variant="contained"
              onClick={() => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  showQuoteForm: 'true'
                });
                handleQuoteFormClick();
              }}
            >
              Add Enquiry
            </Button>
          </AccessWrapper>
        </section>

        {activeStep === 1 && (
          <AccessWrapper show={actions?.read}>
            <EnquiryListing
              quoteState={quoteState}
              setQuoteState={setQuoteState}
              handleOrderSearch={handleOrderSearch}
              filterCreatedBy={filterCreatedBy}
              fetchEnquiryList={fetchEnquiryListWithCounts}
              handleQuoteFormClick={handleQuoteFormClick}
              validatePageChange={validatePageChange}
              itemLimit={itemLimit}
            />
          </AccessWrapper>
        )}

        {activeStep === 2 && (
          <AccessWrapper show={actions?.read}>
            <EnquiryDraftListing
              setQuoteState={setQuoteState}
              quoteState={quoteState}
              handleOrderSearch={handleOrderSearch}
              itemLimit={itemLimit}
              validatePageChange={validatePageChange}
              fetchEnquiryList={fetchEnquiryListWithCounts}
            />
          </AccessWrapper>
        )}

        <AccessWrapper show={actions?.create}>
          {showQuoteForm && (
            <QuoteFormWrapper
              prefill={quotePrefill}
              showQuoteForm={showQuoteForm}
              onClose={onClose}
              onSubmitClose={() => {
                setQuoteState((prevState: IQuoteListingState) => ({
                  ...prevState,
                  showQuoteForm: false
                }));
              }}
            />
          )}

          {showDisclaimer && (
            <SaveToDraftPrompt
              open={showDisclaimer}
              onClose={() =>
                setQuoteState((prevState: IQuoteListingState) => ({
                  ...prevState,
                  showDisclaimer: false
                }))
              }
              onConfirm={() => {
                setQuoteState((prevState: IQuoteListingState) => ({
                  ...prevState,
                  showDisclaimer: false,
                  showQuoteForm: false
                }));
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  showQuoteForm: 'false'
                });
              }}
              onSaveAsDraft={saveAsDraft}
              message="Are you sure you want to close?"
            />
          )}
        </AccessWrapper>
      </main>
    </AccessWrapper>
  );
};

export default QuoteHome;

interface PaginationNavigatorProps {
  currentPageNumber: number;
  count: number;
  itemLimit: number;
  validatePageChange: (event: any, value: number) => void;
}

const PaginationNavigator = (props: PaginationNavigatorProps) => {
  const { currentPageNumber, count, itemLimit, validatePageChange } = props;
  return (
    <div className={css.paginationFilter}>
      <ToolTip title="Previous Page">
        <ArrowBackIosIcon
          sx={{
            height: '40px',
            cursor: currentPageNumber > 1 ? 'pointer' : 'default',
            color: currentPageNumber > 1 ? '#000000' : '#e2e2e2'
          }}
          onClick={() => validatePageChange(null, currentPageNumber - 1)}
        />
      </ToolTip>
      <Typography className={css.pageOfTotalPage}>
        {currentPageNumber} of {Math.ceil(count / itemLimit)}
      </Typography>
      <ToolTip title="Next Page">
        <ArrowForwardIosIcon
          sx={{
            height: '40px',
            cursor: currentPageNumber < Math.ceil(count / itemLimit) ? 'pointer' : 'default',
            color: currentPageNumber < Math.ceil(count / itemLimit) ? '#000000' : '#e2e2e2'
          }}
          onClick={() => validatePageChange(null, currentPageNumber + 1)}
        />
      </ToolTip>
    </div>
  );
};

export { PaginationNavigator };
